import { Directive, ElementRef, EventEmitter, HostListener, OnDestroy, Output, Renderer2 } from '@angular/core'

const LONG_PRESS_MS = 500
// Real touch/mouse jitter is usually a couple px; anything past this means the user is
// dragging, not holding still, so the long-press timer should stop waiting.
const MOVE_CANCEL_THRESHOLD_PX = 10
const MAX_DRAG_PX = 72
const COMMIT_THRESHOLD_PX = 40

/**
 * WhatsApp-style bubble gestures via the Pointer Events API (unifies touch + mouse, no library):
 * drag right to reveal/commit a reply, or hold still to open the long-press actions menu.
 */
@Directive({
    selector: '[appChatBubbleGesture]',
})
export class ChatBubbleGestureDirective implements OnDestroy {
    @Output() swipeReply = new EventEmitter<void>()
    @Output() longPress = new EventEmitter<void>()

    private startX = 0
    private startY = 0
    private dragging = false
    private isHorizontalDrag = false
    // For mouse pointers, `pointermove` fires continuously on hover regardless of whether any
    // button is pressed — without this guard, the very first hover-move after page load would
    // read as a huge "drag" from the default (0, 0) start position.
    private pointerActive = false
    private longPressTimer: ReturnType<typeof setTimeout> | null = null

    constructor(
        private el: ElementRef<HTMLElement>,
        private renderer: Renderer2,
    ) {}

    @HostListener('pointerdown', ['$event'])
    onPointerDown(event: PointerEvent): void {
        this.startX = event.clientX
        this.startY = event.clientY
        this.dragging = false
        this.isHorizontalDrag = false
        this.pointerActive = true
        this.clearLongPressTimer()
        this.longPressTimer = setTimeout(() => {
            this.longPressTimer = null
            this.longPress.emit()
        }, LONG_PRESS_MS)
    }

    @HostListener('pointermove', ['$event'])
    onPointerMove(event: PointerEvent): void {
        if (!this.pointerActive) return

        const dx = event.clientX - this.startX
        const dy = event.clientY - this.startY

        if (!this.dragging && (Math.abs(dx) > MOVE_CANCEL_THRESHOLD_PX || Math.abs(dy) > MOVE_CANCEL_THRESHOLD_PX)) {
            // Real movement, not a hold — stop waiting to fire the long-press menu.
            this.clearLongPressTimer()
            this.dragging = true
            this.isHorizontalDrag = Math.abs(dx) > Math.abs(dy)

            // Deferred until real drag motion is confirmed (not on every pointerdown) — capturing
            // unconditionally would redirect the browser's click dispatch to this element for any
            // plain tap too, breaking nested interactive content (e.g. the @mention spans and the
            // reply-quote block inside the bubble). A fast/long drag can end with the pointer
            // physically over a different element (the card has visually translated out from under
            // it), so pointermove/pointerup would silently stop firing here without capture —
            // leaving the card's transform stuck mid-drag forever.
            this.el.nativeElement.setPointerCapture?.(event.pointerId)
        }

        if (this.dragging && this.isHorizontalDrag) {
            // Only rightward drags reveal the reply affordance; block vertical scroll from being hijacked.
            const clamped = Math.max(0, Math.min(dx, MAX_DRAG_PX))
            this.renderer.setStyle(this.el.nativeElement, 'transition', 'none')
            this.renderer.setStyle(this.el.nativeElement, 'transform', `translateX(${clamped}px)`)
        }
    }

    @HostListener('pointerup', ['$event'])
    onPointerUp(event: PointerEvent): void {
        this.clearLongPressTimer()
        this.releasePointerCapture(event)

        if (this.dragging && this.isHorizontalDrag) {
            const dx = event.clientX - this.startX
            if (dx > COMMIT_THRESHOLD_PX) {
                this.swipeReply.emit()
            }
        }
        this.resetDrag()
    }

    @HostListener('pointercancel', ['$event'])
    onPointerCancel(event: PointerEvent): void {
        this.clearLongPressTimer()
        this.releasePointerCapture(event)
        this.resetDrag()
    }

    // Belt-and-suspenders: if a pointer somehow leaves without an up/cancel ever firing
    // (capture should prevent this, but cheap insurance against it), still reset the drag.
    @HostListener('pointerleave', ['$event'])
    onPointerLeave(event: PointerEvent): void {
        if (!this.dragging) return
        this.clearLongPressTimer()
        this.releasePointerCapture(event)
        this.resetDrag()
    }

    ngOnDestroy(): void {
        this.clearLongPressTimer()
    }

    private resetDrag(): void {
        this.dragging = false
        this.isHorizontalDrag = false
        this.pointerActive = false
        this.renderer.setStyle(this.el.nativeElement, 'transition', 'transform 150ms ease-out')
        this.renderer.setStyle(this.el.nativeElement, 'transform', 'translateX(0)')
    }

    private releasePointerCapture(event: PointerEvent): void {
        if (this.el.nativeElement.hasPointerCapture?.(event.pointerId)) {
            this.el.nativeElement.releasePointerCapture(event.pointerId)
        }
    }

    private clearLongPressTimer(): void {
        if (this.longPressTimer) {
            clearTimeout(this.longPressTimer)
            this.longPressTimer = null
        }
    }
}
